// libs
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import '../assets/css/navbar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightFromBracket, faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import Form from 'react-bootstrap/Form';
// components
import Logo from './logo';
import Loading from './loading';
// config & functions
import { NAV_LINKS } from '../config/roles';
import { useAuth } from '../context/auth-context';
import { useSelectedAccount } from '../context/account-context';

function Navigation({ orgInfo }) {
	const { logout } = useAuth();
	const {
		accounts,
		accountsLoading,
		accountsError,
		selectedAccountId,
		setSelectedAccountId,
		allowAccountSwitch,
		selectedAccount,
		selectedAccountDomain,
	} = useSelectedAccount();
	const location = useLocation();
	const [mobileOpen, setMobileOpen] = useState(false);

	const roleId = Number(orgInfo?.roleId);
	const visibleNavLinks = NAV_LINKS.filter((link) => link.roles.includes(roleId));

	useEffect(() => {
		document.body.classList.add('admin-has-sidebar');
		return () => {
			document.body.classList.remove('admin-has-sidebar');
		};
	}, []);

	useEffect(() => {
		setMobileOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		const onResize = () => {
			if (window.innerWidth >= 992) {
				setMobileOpen(false);
			}
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	const handleLogout = async () => {
		await logout();
		window.location.href = '/login';
	};

	const accountQuery = selectedAccountId ? `?account=${selectedAccountId}` : '';

	const navHref = (path) => `${path}${accountQuery}`;

	const isNavActive = (path) => {
		const { pathname } = location;
		if (path === '/') {
			return pathname === '/' || pathname === '/dashboard';
		}
		return pathname === path;
	};

	const sellerAccountLabel = selectedAccount?.label || orgInfo?.orgName || orgInfo?.domain || 'Seller account';
	const accountStripName = selectedAccount?.label || (!allowAccountSwitch ? sellerAccountLabel : 'No account selected');
	const accountStripDomain = selectedAccount?.domain || (!allowAccountSwitch ? selectedAccountDomain : '');

	const renderAccountAndLogout = () => (
		<div className="admin-navbar-utils">
			<div className="admin-navbar-account">
				{accountsLoading ? (
					<span className="d-inline-block" style={{ minWidth: '160px' }}><Loading margin={0} /></span>
				) : accountsError ? (
					<span className="admin-navbar-error">{accountsError}</span>
				) : allowAccountSwitch ? (
					<Form.Select
						size="sm"
						id="global-seller-account"
						value={selectedAccountId}
						onChange={(e) => setSelectedAccountId(e.target.value)}
						aria-label="Seller account"
					>
						<option value="">Choose account...</option>
						{accounts.map((d) => (
							<option key={d.pk_id} value={d.pk_id}>{d.label}</option>
						))}
					</Form.Select>
				) : (
					<span className="admin-navbar-account-label">{sellerAccountLabel}</span>
				)}
			</div>
			<button
				type="button"
				className="admin-navbar-logout-btn"
				onClick={handleLogout}
				aria-label="Logout"
				title="Logout"
			>
				<FontAwesomeIcon icon={faRightFromBracket} />
				<span>Logout</span>
			</button>
		</div>
	);

	return (
		<>
			<div className="admin-account-strip" role="status" aria-live="polite">
				<span className="admin-account-strip-label">CURRENT ACCOUNT</span>
				<strong className="admin-account-strip-name">
					{accountsLoading && allowAccountSwitch ? 'Loading accounts...' : accountStripName}
				</strong>
				{accountStripDomain && <span className="admin-account-strip-domain">{accountStripDomain}</span>}
				{!selectedAccount && allowAccountSwitch && !accountsLoading && (
					<span className="admin-account-strip-hint">Choose an account from the sidebar</span>
				)}
			</div>
			<button
				type="button"
				className="admin-navbar-mobile-toggle"
				onClick={() => setMobileOpen((open) => !open)}
				aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
				aria-expanded={mobileOpen}
			>
				<FontAwesomeIcon icon={mobileOpen ? faXmark : faBars} />
			</button>
			{mobileOpen ? (
				<button
					type="button"
					className="admin-navbar-backdrop"
					aria-label="Close navigation"
					onClick={() => setMobileOpen(false)}
				/>
			) : null}
			<aside className={`admin-navbar${mobileOpen ? ' is-open' : ''}`}>
				<div className="admin-navbar-brand">
					<Logo />
				</div>
				<nav className="admin-navbar-source" aria-label="Main navigation">
					{visibleNavLinks.map((link) => (
						<a
							key={link.label}
							href={navHref(link.path)}
							className={`admin-navbar-source-link${isNavActive(link.path) ? ' active' : ''}`}
							aria-current={isNavActive(link.path) ? 'page' : undefined}
						>
							{link.label}
						</a>
					))}
				</nav>
				{renderAccountAndLogout()}
			</aside>
		</>
	);
}

export default Navigation;
